// Post-processing: bloom, tone mapping, exposure, planet curvature, CRT/VHS
// composite. Conforms to GDD §11.1.

import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { CrtCompositeShader } from './shaders/crt-composite.js';
import { PlanetCurvatureShader } from './shaders/planet-curvature.js';
import { HighlightRolloffShader } from './shaders/highlight-rolloff.js';
import { HueTonemapShader } from './shaders/hue-tonemap.js';
import { LomoLensShader } from './shaders/lomo-lens.js';
import { VcrGlitchShader } from './shaders/vcr-glitch.js';

const DEFAULT_BLOOM_STRENGTH = 0.35;
const REDUCED_BLOOM_STRENGTH = 0.18;
// Tight bloom kernel: the halo hugs the sprite (≤1.5× sprite radius) instead
// of fusing clumps into one glow mass.
const BLOOM_RADIUS = 0.1;
// Luminance budget (linear HDR, post-rolloff, Rec.601 luma — the weights the
// UnrealBloomPass high-pass uses). The threshold sits in the gap between
// enemy body FILLS (never bloom → clumps keep per-unit silhouettes) and the
// hot edge/core layers (bloom as the neon read). instances.ts holds the
// per-family multipliers that land these values:
//   enemy body fill (×0.45 dim)      ≤ 0.47   below — no fill bloom
//   enemy facet wire (thin lines)    ≤ 0.79   mixed — interior hot detail
//   enemy rim edge (lift ≥0.55)      0.53–0.82 above on mid/near planes
//   boss rim                         0.62–0.74 above
//   ally projectile (×1.6)           ≈ 0.62   above
//   enemy projectile / xp-hp mote    0.49–0.59 below (unchanged behavior)
//   pickup coin rim / kill flashes   0.80+    above
// Additive kill-flash stacks are still capped hue-true at max-channel 0.85 by
// the rolloff; the hue-preserving tonemap after bloom grades them
// red→orange→warm-white instead of clipping to salmon-white.
const BLOOM_THRESHOLD = 0.6;

function clamp01(v: number): number {
  return Math.max(0, Math.min(1, v));
}

export class PostProcess {
  private composer: EffectComposer | null = null;
  private bloomPass: UnrealBloomPass | null = null;
  private tonemapPass: ShaderPass | null = null;
  private crtPass: ShaderPass | null = null;
  private planetPass: ShaderPass | null = null;
  private lomoPass: ShaderPass | null = null;
  private vcrPass: ShaderPass | null = null;
  private scene: THREE.Scene | null = null;
  private camera: THREE.Camera | null = null;
  private bloomEnabled = true;
  private bloomStrength = DEFAULT_BLOOM_STRENGTH;
  // Structural toggles (bloom on/off, CRT on/off, planet curvature on/off)
  // force a composer rebuild; intensity-only changes update uniforms on the
  // live passes instead.
  private structureDirty = false;

  private planetEnabled = false;
  private planetStrength = PlanetCurvatureShader.uniforms.strength.value;
  private lomoEnabled = true;
  private lomoStrength = LomoLensShader.uniforms.strength.value;
  private vcrEnabled = true;
  private vcrStrength = VcrGlitchShader.uniforms.strength.value;
  private vcrGlitchStrength = VcrGlitchShader.uniforms.glitchStrength.value;
  private crtEnabled = false;
  private crtScanlines = CrtCompositeShader.uniforms.scanlineIntensity.value;
  private crtAberration = CrtCompositeShader.uniforms.chromaticAberration.value;
  private crtNoise = CrtCompositeShader.uniforms.noiseIntensity.value;
  private crtBarrelWarp = CrtCompositeShader.uniforms.barrelWarp.value;
  private crtVhsTracking = false;
  private readonly startTime = performance.now();
  private lastRenderMs = this.startTime;
  private exposurePulse = 0;

  /** Brief full-frame brightness lift (hits/deaths/explosions). */
  pulseExposure(amount: number): void {
    this.exposurePulse = Math.max(this.exposurePulse, amount);
  }

  /** Storm-driven edge blur: adds on top of the configured lomo strength. */
  setStormLomoBoost(boost: number): void {
    const s = Math.min(1, this.lomoStrength + Math.max(0, Math.min(1, boost)));
    if (this.lomoPass) {
      this.lomoPass.uniforms.strength.value = s;
    }
  }

  constructor(
    private readonly renderer: THREE.WebGLRenderer,
    bloomIntensity: number,
    private reducedFlashes = false
  ) {
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.0;
    this.setIntensity(bloomIntensity);
  }

  setReducedFlashes(enabled: boolean): void {
    this.reducedFlashes = enabled;
    this.updateBloomStrength();
  }

  setIntensity(intensity: number): void {
    // intensity is 0..1 from settings; 0 disables bloom pass entirely.
    const wasEnabled = this.bloomEnabled;
    this.bloomEnabled = intensity > 0;
    this.bloomStrength = this.reducedFlashes
      ? REDUCED_BLOOM_STRENGTH * intensity
      : DEFAULT_BLOOM_STRENGTH * intensity;
    if (this.bloomPass) {
      this.bloomPass.strength = this.bloomStrength;
    }
    if (wasEnabled !== this.bloomEnabled) {
      this.structureDirty = true;
    }
  }

  setCrtEnabled(enabled: boolean): void {
    if (this.crtEnabled === enabled) return;
    this.crtEnabled = enabled;
    this.structureDirty = true;
  }

  setPlanetCurvature(enabled: boolean, strength?: number): void {
    if (this.planetEnabled !== enabled) {
      this.planetEnabled = enabled;
      this.structureDirty = true;
    }
    if (strength !== undefined) {
      this.planetStrength = Math.max(0, Math.min(1, strength));
      if (this.planetPass) {
        this.planetPass.uniforms.strength.value = this.planetStrength;
      }
    }
  }

  setLomoLens(enabled: boolean, strength?: number): void {
    if (this.lomoEnabled !== enabled) {
      this.lomoEnabled = enabled;
      this.structureDirty = true;
    }
    if (strength !== undefined) {
      this.lomoStrength = Math.max(0, Math.min(1, strength));
      if (this.lomoPass) {
        this.lomoPass.uniforms.strength.value = this.lomoStrength;
      }
    }
  }

  setVcrGlitch(enabled: boolean, strength?: number, glitchStrength?: number): void {
    if (this.vcrEnabled !== enabled) {
      this.vcrEnabled = enabled;
      this.structureDirty = true;
    }
    if (strength !== undefined) {
      this.vcrStrength = Math.max(0, Math.min(1, strength));
      if (this.vcrPass) {
        this.vcrPass.uniforms.strength.value = this.vcrStrength;
      }
    }
    if (glitchStrength !== undefined) {
      this.vcrGlitchStrength = Math.max(0, Math.min(1, glitchStrength));
      if (this.vcrPass) {
        this.vcrPass.uniforms.glitchStrength.value = this.vcrGlitchStrength;
      }
    }
  }

  setScanlineIntensity(intensity: number): void {
    this.crtScanlines = clamp01(intensity);
    if (this.crtPass) {
      this.crtPass.uniforms.scanlineIntensity.value = this.crtScanlines;
    }
  }

  setChromaticAberration(intensity: number): void {
    this.crtAberration = clamp01(intensity);
    if (this.crtPass) {
      this.crtPass.uniforms.chromaticAberration.value = this.crtAberration;
    }
  }

  setNoise(intensity: number): void {
    this.crtNoise = clamp01(intensity);
    if (this.crtPass) {
      this.crtPass.uniforms.noiseIntensity.value = this.crtNoise;
    }
  }

  setBarrelWarp(intensity: number): void {
    this.crtBarrelWarp = clamp01(intensity);
    if (this.crtPass) {
      this.crtPass.uniforms.barrelWarp.value = this.crtBarrelWarp;
    }
  }

  setVhsTracking(enabled: boolean): void {
    this.crtVhsTracking = enabled;
    if (this.crtPass) {
      this.crtPass.uniforms.vhsTracking.value = enabled ? 1 : 0;
    }
  }

  resize(width: number, height: number): void {
    if (this.composer) {
      this.composer.setSize(width, height);
    }
    if (this.crtPass) {
      this.crtPass.uniforms.resolution.value.copy(this.drawingBufferSize());
    }
  }

  render(scene: THREE.Scene, camera: THREE.Camera): void {
    // Hit/death brightness pulse: a brief exposure lift, decaying fast.
    const now = performance.now();
    const dt = Math.min(0.1, (now - this.lastRenderMs) / 1000);
    this.lastRenderMs = now;
    if (this.exposurePulse > 0.001) {
      this.renderer.toneMappingExposure = 1.0 + this.exposurePulse * 0.3;
      this.exposurePulse *= Math.exp(-dt * 9);
    } else {
      this.exposurePulse = 0;
      this.renderer.toneMappingExposure = 1.0;
    }
    // The composer path ignores renderer.toneMappingExposure (tone mapping on
    // a render target is a no-op), so feed the pulse to the tonemap pass.
    if (this.tonemapPass) {
      this.tonemapPass.uniforms.exposure.value = this.renderer.toneMappingExposure;
    }

    if (!this.bloomEnabled && !this.crtEnabled && !this.planetEnabled && !this.lomoEnabled && !this.vcrEnabled) {
      this.renderer.render(scene, camera);
      return;
    }
    if (!this.composer || this.scene !== scene || this.camera !== camera || this.structureDirty) {
      this.disposeComposer();
      this.composer = new EffectComposer(this.renderer);
      this.composer.addPass(new RenderPass(scene, camera));
      // Soft-knee highlight rolloff before bloom so additive stacks shade
      // instead of clipping to a flat white blob.
      this.composer.addPass(new ShaderPass(HighlightRolloffShader));
      const size = new THREE.Vector2();
      this.renderer.getSize(size);
      if (this.bloomEnabled) {
        this.bloomPass = new UnrealBloomPass(
          size,
          this.bloomStrength,
          BLOOM_RADIUS,
          BLOOM_THRESHOLD
        );
        this.composer.addPass(this.bloomPass);
      }
      // Hue-preserving tone map (replaces OutputPass): ACES shoulder on the
      // max channel keeps stacked additive highlights hue-true; also does the
      // linear→sRGB conversion. Everything after operates display-referred.
      this.tonemapPass = new ShaderPass(HueTonemapShader);
      this.tonemapPass.uniforms.exposure.value = this.renderer.toneMappingExposure;
      this.composer.addPass(this.tonemapPass);
      if (this.planetEnabled) {
        // After tone mapping: operates on display-referred pixels, before CRT.
        this.planetPass = new ShaderPass(PlanetCurvatureShader);
        this.planetPass.uniforms.strength.value = this.planetStrength;
        this.composer.addPass(this.planetPass);
      }
      if (this.lomoEnabled) {
        // Lomo lens: vignette + edge blur after curvature, before CRT.
        this.lomoPass = new ShaderPass(LomoLensShader);
        this.lomoPass.uniforms.strength.value = this.lomoStrength;
        this.lomoPass.uniforms.resolution.value.copy(this.drawingBufferSize());
        this.composer.addPass(this.lomoPass);
      }
      if (this.vcrEnabled) {
        // VCR + rare glitch: tracking noise, chroma bleed, occasional bursts.
        this.vcrPass = new ShaderPass(VcrGlitchShader);
        this.vcrPass.uniforms.strength.value = this.vcrStrength;
        this.vcrPass.uniforms.glitchStrength.value = this.vcrGlitchStrength;
        this.vcrPass.uniforms.resolution.value.copy(this.drawingBufferSize());
        this.composer.addPass(this.vcrPass);
      }
      if (this.crtEnabled) {
        // Final pass: operates on display-referred pixels after tone mapping.
        this.crtPass = new ShaderPass(CrtCompositeShader);
        this.crtPass.uniforms.resolution.value.copy(this.drawingBufferSize());
        this.syncCrtUniforms();
        this.composer.addPass(this.crtPass);
      }
      this.scene = scene;
      this.camera = camera;
      this.structureDirty = false;
    }
    if (this.bloomPass) {
      this.bloomPass.strength = this.bloomStrength;
    }
    if (this.crtPass) {
      this.crtPass.uniforms.time.value = (performance.now() - this.startTime) / 1000;
    }
    if (this.vcrPass) {
      this.vcrPass.uniforms.time.value = (performance.now() - this.startTime) / 1000;
    }
    this.composer.render();
  }

  private syncCrtUniforms(): void {
    if (!this.crtPass) return;
    this.crtPass.uniforms.scanlineIntensity.value = this.crtScanlines;
    this.crtPass.uniforms.chromaticAberration.value = this.crtAberration;
    this.crtPass.uniforms.noiseIntensity.value = this.crtNoise;
    this.crtPass.uniforms.barrelWarp.value = this.crtBarrelWarp;
    this.crtPass.uniforms.vhsTracking.value = this.crtVhsTracking ? 1 : 0;
  }

  private drawingBufferSize(): THREE.Vector2 {
    const size = new THREE.Vector2();
    this.renderer.getDrawingBufferSize(size);
    return size;
  }

  private updateBloomStrength(): void {
    if (this.bloomPass) {
      this.bloomPass.strength = this.bloomStrength;
    }
  }

  private disposeComposer(): void {
    if (this.composer) {
      this.composer.dispose();
      this.composer = null;
      this.bloomPass = null;
      this.tonemapPass = null;
      this.crtPass = null;
      this.planetPass = null;
      this.lomoPass = null;
      this.vcrPass = null;
      this.scene = null;
      this.camera = null;
    }
  }

  dispose(): void {
    this.disposeComposer();
  }
}
